// ============================================================================
// Algo gateway client
// ============================================================================

import { ApiClientError, type ApiFailureReport, MAX_CAPTURED_BODY_LENGTH, reportApiFailure } from "~/api/client";
import { getSessionGeneration, getSessionState, reestablishSession, whenSessionSettled } from "~/lib/session-gate";

const getAlgoBaseUrl = (): string => import.meta.env.VITE_ALGO_API_BASE_URL ?? "";

/** Every algo endpoint backs a video surface, so one message covers them all. */
const USER_MESSAGE = "Could not load videos. Please try again.";

/** The algo gateway is FastAPI: `detail` is a string for HTTPException and an array for
 *  validation errors. Never shown to users — diagnostic context only. Every return path is
 *  capped at MAX_CAPTURED_BODY_LENGTH so callers (e.g. the `detail` field passed to
 *  reportApiFailure) inherit the same truncation guarantee as `responseBody`. */
function algoErrorDetail(rawBody: string): string {
  try {
    const detail = (JSON.parse(rawBody) as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail.slice(0, MAX_CAPTURED_BODY_LENGTH);
    if (Array.isArray(detail)) {
      return detail
        .map(e => `${((e as { loc?: unknown[] })?.loc ?? []).join(".")}: ${(e as { msg?: string })?.msg}`)
        .join("; ")
        .slice(0, MAX_CAPTURED_BODY_LENGTH);
    }
    // `null` is JSON's "absent" — treat it like `undefined` and fall through to the raw
    // body, rather than returning the literal string "null". `0`, `false`, and "" are
    // meaningful values and still return above/below via their own branches.
    if (detail !== undefined && detail !== null) return JSON.stringify(detail).slice(0, MAX_CAPTURED_BODY_LENGTH);
  } catch {
    // Guards the whole extraction above (JSON.parse, the array mapping, and
    // JSON.stringify), not just "not JSON" — any failure here falls through to the raw body.
  }
  return rawBody.slice(0, MAX_CAPTURED_BODY_LENGTH);
}

/** What a single attempt produced, with reporting deferred to the caller. */
type AlgoAttempt<T> = { ok: true; data: T } | { ok: false; error: ApiClientError; report: ApiFailureReport };

/**
 * POST `body` to an algo gateway endpoint and return its `content`.
 *
 * The algo service bypasses `ApiClient`, so it inherits none of the session handling that
 * every other request gets. Both halves are therefore implemented here rather than left
 * out, because both have already been paid for in production:
 *
 * - **Waiting for the session.** The gateway authenticates on the `blidz_session_id`
 *   cookie, so calling this before a session exists is a guaranteed 401 — 116 users in a
 *   single 8-hour window.
 * - **Replaying once after a session 401.** A returning visitor takes the optimistic path,
 *   where the gate opens without proving the cookie still works. Without a replay, the ones
 *   whose cookie had expired would get exactly the 401 the wait was added to remove.
 *
 * Callers need no gate of their own.
 *
 * There is deliberately no transport retry and no per-caller branching: an endpoint that needs
 * different session handling gets its own function rather than a flag on this one.
 */
export async function algoRequest<T>(path: string, body: object, readToken: () => string | null): Promise<T> {
  await whenSessionSettled();
  if (getSessionState() === "unavailable") {
    // Nothing is sent: the session flow already reported why, and this request could only
    // come back 401. Reporting here too would turn one failure into several.
    throw new ApiClientError(
      0,
      "SESSION_UNAVAILABLE",
      "No session could be established for this page load",
      USER_MESSAGE
    );
  }

  for (let sessionAttempt = 0; sessionAttempt <= 1; sessionAttempt++) {
    const sessionGeneration = getSessionGeneration();
    // Read after the wait: before the session settles this is null, and passing that
    // through was what made callers re-run once the token arrived.
    const attempt = await sendAlgoRequest<T>(path, readToken(), body);
    if (attempt.ok) return attempt.data;

    // The gateway rejects a missing session before doing any work, so replaying cannot
    // double an effect — the same argument as `SESSION_REPLAY_CODES` in ApiClient.
    const isMissingSession = attempt.error.status === 401 && /blidz_session_id/.test(attempt.error.detail ?? "");
    if (sessionAttempt === 0 && isMissingSession) {
      // Already re-authenticated by someone else while we were in flight: just replay.
      if (getSessionGeneration() !== sessionGeneration) continue;
      await reestablishSession();
      if (getSessionState() === "ready") {
        // Deliberately unreported: transient, and we recovered from it.
        continue;
      }
    }

    reportApiFailure(attempt.error, attempt.report);
    throw attempt.error;
  }

  throw new ApiClientError(0, "SESSION_REPLAY_EXHAUSTED", `algo replay loop fell through for ${path}`);
}

/** One attempt. Builds the failure and its report but never sends either. */
async function sendAlgoRequest<T>(path: string, token: string | null, body: object): Promise<AlgoAttempt<T>> {
  const url = `${getAlgoBaseUrl()}${path}`;

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Only the network call is inside this try, mirroring ApiClient.request: everything
  // caught here is transport level (fetch rejects with a TypeError, or a TimeoutError/
  // AbortError from an abort signal), never a bug in our own response handling below.
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
    });
  } catch (error) {
    const cause = error as Error;
    // Key on `cause.name`, never on message text: Chrome says "Failed to fetch",
    // Safari says "Load failed".
    const isTimeout = cause.name === "TimeoutError" || cause.name === "AbortError";
    const transportError = new ApiClientError(
      0,
      isTimeout ? "TIMEOUT_ERROR" : "NETWORK_ERROR",
      cause.message,
      isTimeout ? "Request timed out. Please try again." : USER_MESSAGE
    );
    return {
      ok: false,
      error: transportError,
      report: { method: "POST", url, status: 0, code: transportError.code, detail: cause.message },
    };
  }

  if (!res.ok) {
    // Read the body once — res.json() would consume the stream, and a later res.text()
    // fallback would throw "body stream already read".
    const rawBody = await res.text();
    const detail = algoErrorDetail(rawBody);
    const apiError = new ApiClientError(res.status, "algo_error", detail, USER_MESSAGE);
    return {
      ok: false,
      error: apiError,
      report: {
        method: "POST",
        url,
        status: res.status,
        code: apiError.code,
        responseBody: rawBody.slice(0, MAX_CAPTURED_BODY_LENGTH),
        detail,
      },
    };
  }

  const json = await res.json();
  return { ok: true, data: (json?.content ?? json) as T };
}
